// Apolo IDE — multi-pane workspace layout (Code tab, Cursor-style split).

import { useCallback, useRef, useState } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';
import {
  isSessionDragEvent,
  readSessionIdFromDataTransfer,
} from '../../lib/sessionDrag.js';
import { useAgentStore } from '../../store/useAgentStore.js';
import SessionWorkspace from './SessionWorkspace.jsx';
import WorkspaceTabsBar from './WorkspaceTabsBar.jsx';
const DIVIDER_HIT_PX = 10;

function PaneChrome({ session, paneId, focused, showClose, onClose, maximized, onToggleMaximize }) {
  const title = session?.name || 'Sessão';
  return (
    <div
      className={`flex shrink-0 items-center gap-2 border-b px-2 py-1.5 ${
        focused
          ? 'border-apolo-borderSubtle bg-apolo-surface2'
          : 'border-apolo-borderSubtle bg-apolo-surface1'
      }`}
    >
      <span
        className={`min-w-0 flex-1 truncate text-xs font-medium ${
          focused ? 'text-apolo-textPrimary' : 'text-apolo-textSecondary'
        }`}
        title={title}
      >
        {title}
      </span>
      {onToggleMaximize && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleMaximize(paneId);
          }}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/60"
          aria-label={maximized ? `Restaurar painel ${title}` : `Maximizar painel ${title}`}
          aria-pressed={maximized}
          title={maximized ? 'Restaurar (tamanho mínimo)' : 'Maximizar (tamanho máximo)'}
        >
          {maximized ? <Minimize2 size={13} aria-hidden="true" /> : <Maximize2 size={13} aria-hidden="true" />}
        </button>
      )}
      {showClose && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose(paneId);
          }}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/60"
          aria-label={`Fechar painel ${title}`}
          title="Fechar painel"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function PaneDivider({ onResize, splitPct }) {
  const startRef = useRef(null);
  const ariaNow = Math.round((splitPct || 0.5) * 100);

  function onPointerDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    startRef.current = { x: e.clientX };
    e.currentTarget.setPointerCapture(e.pointerId);

    function onMove(ev) {
      if (!startRef.current) return;
      onResize(ev.clientX - startRef.current.x);
      startRef.current.x = ev.clientX;
    }

    function onUp() {
      startRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuemin={15}
      aria-valuemax={85}
      aria-valuenow={ariaNow}
      aria-label="Redimensionar painéis"
      tabIndex={0}
      className="group relative z-10 flex-shrink-0 cursor-col-resize select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
      style={{ width: DIVIDER_HIT_PX }}
      onPointerDown={onPointerDown}
    >
      <div className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 rounded-full bg-apolo-border/80 transition group-hover:bg-apolo-accent/70" />
    </div>
  );
}

export default function WorkspacePaneLayout() {
  const workspacePanes = useAgentStore((s) => s.code.workspacePanes);
  const focusedPaneId = useAgentStore((s) => s.code.focusedPaneId);
  const maximizedPaneId = useAgentStore((s) => s.code.maximizedPaneId);
  const sessions = useAgentStore((s) => s.code.sessions);
  const focusPane = useAgentStore((s) => s.focusPane);
  const closePane = useAgentStore((s) => s.closePane);
  const setPaneSizes = useAgentStore((s) => s.setPaneSizes);
  const togglePaneMaximize = useAgentStore((s) => s.togglePaneMaximize);
  const openSessionInWorkspace = useAgentStore((s) => s.openSessionInWorkspace);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);

  // (Pedro) max/min: when a pane is maximized it fills the workspace and the
  // others are hidden; the tabs strip is hidden too (restore to see them).
  const maximizedPane =
    maximizedPaneId ? workspacePanes.find((p) => p.id === maximizedPaneId) : null;
  const renderPanes = maximizedPane ? [maximizedPane] : workspacePanes;
  const multi = workspacePanes.length > 1;

  const containerRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleResizeDivider = useCallback(
    (dividerIndex, deltaPx) => {
      const container = containerRef.current;
      if (!container || deltaPx === 0) return;
      const width = container.getBoundingClientRect().width;
      if (width <= 0) return;

      const deltaFraction = deltaPx / width;
      const panes = useAgentStore.getState().code.workspacePanes;
      if (dividerIndex < 0 || dividerIndex >= panes.length - 1) return;

      const left = panes[dividerIndex].sizeFraction + deltaFraction;
      const right = panes[dividerIndex + 1].sizeFraction - deltaFraction;
      const min = 0.15;

      if (left < min || right < min) return;

      const fractions = panes.map((p) => p.sizeFraction);
      fractions[dividerIndex] = left;
      fractions[dividerIndex + 1] = right;
      setPaneSizes(fractions);
    },
    [setPaneSizes],
  );

  function handleDragOver(e) {
    if (!isSessionDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  }

  function handleDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  }

  function handleDrop(e) {
    // BUG (Pedro): drop de ARQUIVO na aba Code não anexava. Este handler roda em
    // onDropCapture (fase de captura, ANTES do composer) e dava stopPropagation
    // incondicional → matava o drop de arquivo do composer. O guard existia no
    // handleDragOver mas faltava aqui: drag que NÃO é de sessão (ex.: Files) passa
    // reto pro composer, sem consumir o evento.
    if (!isSessionDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const sessionId = readSessionIdFromDataTransfer(e.dataTransfer);
    if (!sessionId) return;

    const result = openSessionInWorkspace(sessionId, { mode: 'split' });
    if (!result.ok && result.error === 'max_panes') {
      window.dispatchEvent(
        new CustomEvent('apolo:toast', {
          detail: {
            kind: 'warning',
            message: `Máximo de ${result.max} sessões lado a lado`,
          },
        }),
      );
    }
  }

  function handlePaneBack(pane) {
    const panes = useAgentStore.getState().code.workspacePanes;
    if (panes.length <= 1) {
      setActiveSession(null);
      return;
    }
    closePane(pane.id);
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Cowork composer (E3): tabs over the open panes — each open folder/session
          is a tab. Click focuses its pane; × closes it. (Pedro) — only worth
          showing for 2+ panes; a single pane already names itself in the chat's
          ctx-tab, so the strip + the per-pane chrome below are redundant noise. */}
      {multi && !maximizedPane && (
        <WorkspaceTabsBar
          panes={workspacePanes}
          focusedPaneId={focusedPaneId}
          sessions={sessions}
          onFocus={focusPane}
          onClose={(id) => handlePaneBack({ id })}
        />
      )}
      <div
        ref={containerRef}
        data-testid="workspace-drop-zone"
        className="relative flex min-h-0 min-w-0 flex-1 flex-row"
        onDragOver={handleDragOver}
      onDragOverCapture={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDropCapture={handleDrop}
    >
      {dragOver && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-apolo-accent/10 backdrop-blur-[1px]"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
          }}
        >
          <span className="rounded-md border border-apolo-accent/50 bg-apolo-surface/95 px-3 py-1.5 text-sm font-medium text-apolo-accent shadow-sm">
            Solte para dividir e abrir esta sessão ao lado
          </span>
        </div>
      )}

      {renderPanes.map((pane, index) => {
        const session = sessions.find((s) => s.id === pane.sessionId);
        const focused = pane.id === focusedPaneId;
        const widthPct = maximizedPane ? '100%' : `${(pane.sizeFraction || 0) * 100}%`;

        return (
          <div key={pane.id} className="contents">
            {index > 0 && !maximizedPane && (
              <PaneDivider
                splitPct={workspacePanes
                  .slice(0, index)
                  .reduce((sum, p) => sum + (p.sizeFraction || 0), 0)}
                onResize={(delta) => handleResizeDivider(index - 1, delta)}
              />
            )}
            <div
              role="group"
              aria-label={session ? `Painel: ${session.name}` : 'Painel vazio'}
              className={`flex min-h-0 min-w-0 flex-col overflow-hidden border bg-apolo-surface1 transition-colors ${
                focused && multi
                  ? 'border-apolo-borderStrong'
                  : 'border-apolo-borderSubtle'
              }`}
              style={{ width: widthPct, flex: `0 0 ${widthPct}` }}
              onPointerDownCapture={() => focusPane(pane.id)}
            >
              {multi && (
                <PaneChrome
                  session={session}
                  paneId={pane.id}
                  focused={focused}
                  showClose={multi}
                  onClose={closePane}
                  maximized={pane.id === maximizedPaneId}
                  onToggleMaximize={togglePaneMaximize}
                />
              )}
              {session ? (
                <SessionWorkspace session={session} onBack={() => handlePaneBack(pane)} />
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-apolo-textTertiary">
                  Sessão indisponível
                </div>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
